/** Mirrors crm BidChannel / BidOrigin / BidStatus / BidJobType / BidVsMedian (DEV13-72). */
export const BID_CHANNELS = [
  { value: 'upwork', label: 'Upwork' },
  { value: 'fiverr', label: 'Fiverr' },
  { value: 'rfp', label: 'RFP / tender' },
  { value: 'other', label: 'Other' },
] as const;
export type BidChannel = (typeof BID_CHANNELS)[number]['value'];

export const BID_ORIGINS = [
  { value: 'sync', label: 'Synced' },
  { value: 'manual', label: 'Manual' },
] as const;
export type BidOrigin = (typeof BID_ORIGINS)[number]['value'];

export const BID_STATUSES = [
  { value: 'submitted', label: 'Submitted' },
  { value: 'viewed', label: 'Viewed' },
  { value: 'replied', label: 'Replied' },
  { value: 'interviewing', label: 'Interviewing' },
  { value: 'hired', label: 'Hired' },
  { value: 'declined', label: 'Declined' },
  { value: 'closed', label: 'Closed' },
  { value: 'withdrawn', label: 'Withdrawn' },
] as const;
export type BidStatus = (typeof BID_STATUSES)[number]['value'];

export const BID_JOB_TYPES = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'fixed', label: 'Fixed price' },
] as const;
export type BidJobType = (typeof BID_JOB_TYPES)[number]['value'];

export const BID_VS_MEDIAN = [
  { value: 'below', label: 'Below median' },
  { value: 'at', label: 'At median' },
  { value: 'above', label: 'Above median' },
] as const;
export type BidVsMedian = (typeof BID_VS_MEDIAN)[number]['value'];

export type BidConvertedBy = 'none' | 'sync' | 'manual';

/** Hourly bid-rate bands — `value` is the label the backend groups and filters by. */
export const BID_RATE_BANDS = [
  { value: '<25', label: '$<25/h' },
  { value: '25-39', label: '$25-39/h' },
  { value: '40-59', label: '$40-59/h' },
  { value: '60-79', label: '$60-79/h' },
  { value: '80+', label: '$80+/h' },
  { value: 'fixed', label: 'Fixed price' },
] as const;
export type BidRateBand = (typeof BID_RATE_BANDS)[number]['value'];

export const hireRateMinLabel = (pct: string): string => `${pct}% or more`;
/** Minimum client hire-rate thresholds offered by the list filter (percent). */
export const BID_HIRE_RATE_MINS: readonly { value: string; label: string }[] = ['25', '50', '75', '90'].map(
  (value) => ({ value, label: hireRateMinLabel(value) }),
);

/** Roll-up dimensions (`?group_by=`) — order mirrors the backend. */
export const BID_ROLLUP_DIMENSIONS = [
  { value: 'week', label: 'Week' },
  { value: 'channel', label: 'Channel' },
  { value: 'owner', label: 'Owner' },
  { value: 'rate_band', label: 'Rate band' },
  { value: 'bid_vs_median', label: 'Bid vs median' },
  { value: 'boosted', label: 'Boosted' },
] as const;
export type BidRollupDimension = (typeof BID_ROLLUP_DIMENSIONS)[number]['value'];

export const channelLabel = (s: string): string =>
  BID_CHANNELS.find((x) => x.value === s)?.label ?? s;
export const originLabel = (s: string): string =>
  BID_ORIGINS.find((x) => x.value === s)?.label ?? s;
export const bidStatusLabel = (s: string): string =>
  BID_STATUSES.find((x) => x.value === s)?.label ?? s;
export const jobTypeLabel = (s: string): string =>
  BID_JOB_TYPES.find((x) => x.value === s)?.label ?? s;
export const vsMedianLabel = (s: string): string =>
  BID_VS_MEDIAN.find((x) => x.value === s)?.label ?? s;
export const rateBandLabel = (s: string): string =>
  BID_RATE_BANDS.find((x) => x.value === s)?.label ?? s;
export const rollupDimensionLabel = (s: string): string =>
  BID_ROLLUP_DIMENSIONS.find((x) => x.value === s)?.label ?? s;

/** Narrow a `{value,label}[]` constant to a zod-ready tuple of its values. */
export const valuesOf = <T extends readonly { value: string }[]>(list: T) =>
  list.map((x) => x.value) as unknown as [T[number]['value'], ...T[number]['value'][]];

export function bidStatusVariant(
  s: string,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (s === 'hired') return 'default';
  if (s === 'declined' || s === 'closed' || s === 'withdrawn') return 'destructive';
  if (s === 'replied' || s === 'interviewing' || s === 'viewed') return 'secondary';
  return 'outline'; // submitted
}
